import { useEffect, useState, useCallback } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatApiError, downloadInvoicePdf, downloadPdf, fileUrl } from "@/lib/api";
import OrderMiniMap from "@/components/OrderMiniMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Package, Users, Tag, Truck, FileText, CreditCard, Settings as SettingsIcon,
  Plus, Trash2, Check, AlertTriangle, Download, Pencil, Repeat, Pause, Play, Mail, MessageSquare, QrCode, Scale, History, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const money = (n) => "£" + Number(n || 0).toFixed(2);
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "recurring", label: "Recurring", icon: Repeat },
  { key: "clients", label: "Clients", icon: Users },
  { key: "pricing", label: "Price list", icon: Tag },
  { key: "drivers", label: "Drivers & jobs", icon: Truck },
  { key: "invoices", label: "Invoices", icon: FileText },
  { key: "subscription", label: "Subscription", icon: CreditCard },
  { key: "account", label: "Settings", icon: SettingsIcon },
];
const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700", collected: "bg-blue-100 text-blue-700",
  processing: "bg-amber-100 text-amber-700", ready: "bg-teal-100 text-teal-700",
  out_for_delivery: "bg-indigo-100 text-indigo-700", completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};


function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 font-heading text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/business/stats").then((r) => setStats(r.data));
    api.get("/business/me").then((r) => setMe(r.data));
  }, []);
  if (!stats || !me) return <div className="py-10 text-sm text-slate-400">Loading…</div>;
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-slate-900">{me.name}</h1>
      <p className="mt-1 text-sm text-slate-500">Here&rsquo;s what&rsquo;s happening across your laundry today.</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active orders" value={stats.active_orders} testid="stat-active-orders" />
        <StatCard label="Total orders" value={stats.total_orders} testid="stat-total-orders" />
        <StatCard label="Clients" value={stats.clients} testid="stat-clients" />
        <StatCard label="Drivers" value={stats.drivers} testid="stat-drivers" />
        <StatCard label="Revenue (paid)" value={money(stats.revenue)} testid="stat-revenue" />
        <StatCard label="Outstanding" value={money(stats.outstanding)} testid="stat-outstanding" />
        <StatCard label="Plan" value={me.plan?.name || "—"} testid="stat-plan" />
        <StatCard label="Orders this month" value={me.usage?.orders_this_month ?? 0} testid="stat-orders-month" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="top-clients-widget">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-slate-900">Top clients</h2>
            <span className="text-xs text-slate-400">by amount billed</span>
          </div>
          <div className="mt-4 space-y-3">
            {(stats.top_clients || []).map((c) => {
              const max = stats.top_clients[0]?.billed || 1;
              return (
                <div key={c.client_id} data-testid={`top-client-${c.client_id}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">{c.name}</span>
                    <span className="text-slate-500">{money(c.billed)} <span className="text-amber-600">· {money(c.outstanding)} due</span></span>
                  </div>
                  <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-brand" style={{ width: `${Math.max(6, (c.billed / max) * 100)}%` }} />
                  </div>
                </div>
              );
            })}
            {!(stats.top_clients || []).length && <p className="text-sm text-slate-400">No billing activity yet. Create an invoice to see your top clients here.</p>}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-950 p-5 text-white" data-testid="cashflow-widget">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Cash flow</p>
          <p className="mt-2 text-sm text-slate-300">Collected</p>
          <p className="font-heading text-2xl font-bold text-teal-300">{money(stats.revenue)}</p>
          <p className="mt-4 text-sm text-slate-300">Outstanding</p>
          <p className="font-heading text-2xl font-bold text-amber-400">{money(stats.outstanding)}</p>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- Orders -------------------------------- */

export default Overview;
